import { useState, useEffect } from 'react';
import {
  ArrowLeftRight,
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Truck,
  PackageCheck,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  AlertTriangle,
  ArrowRight,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { invokeAuthenticatedRpc } from '@/lib/authenticatedRpc';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useLocations } from '@/hooks/useLocations';
import { toast } from 'sonner';
import { useLocationStore } from '@/store/location';
import { subscribeToGoodsTransfers, unsubscribe } from '@/lib/realtime';

interface GoodsTransfer {
  id: string;
  organization_id: string;
  from_location_id: string;
  to_location_id: string;
  transfer_number: string;
  status: 'pending' | 'in_transit' | 'received' | 'partially_received' | 'cancelled';
  created_by: string | null;
  shipped_by: string | null;
  received_by: string | null;
  shipped_at: string | null;
  received_at: string | null;
  expected_date: string | null;
  notes: string | null;
  shipping_notes: string | null;
  receiving_notes: string | null;
  created_at: string;
  from_location?: { id: string; name: string } | null;
  to_location?: { id: string; name: string } | null;
  creator?: { first_name: string | null; last_name: string | null } | null;
}

interface GoodsTransferItem {
  id: string;
  transfer_id: string;
  product_id: string | null;
  raw_item_id: string | null;
  quantity_sent: number;
  quantity_received: number | null;
  variance_reason: string | null;
  product?: { id: string; name: string; sku: string | null } | null;
  raw_item?: { id: string; name: string; sku: string | null; unit_of_measure: string } | null;
}

interface TransferSourceAvailability {
  item_key: string;
  item_type: 'product' | 'raw';
  item_id: string;
  item_name: string;
  sku: string | null;
  unit_of_measure: string;
  quantity_on_hand: number;
  quantity_reserved: number;
  quantity_available: number;
  quantity_boh: number | null;
  quantity_foh: number | null;
}

type TransferFormItem = {
  item_key: string;
  quantity: string;
};

const getTransferItemName = (item: GoodsTransferItem) =>
  item.product?.name || item.raw_item?.name || 'Unknown inventory item';

const getTransferItemSku = (item: GoodsTransferItem) =>
  item.product?.sku || item.raw_item?.sku;

const getTransferItemKey = (item: GoodsTransferItem) =>
  item.product_id ? `product:${item.product_id}` : `raw:${item.raw_item_id}`;

const formatQuantity = (value: number) => value.toLocaleString(undefined, {
  maximumFractionDigits: 3,
});

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Clock },
  in_transit: { label: 'In Transit', color: 'bg-blue-100 text-blue-700', icon: Truck },
  received: { label: 'Received', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  partially_received: { label: 'Partial', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
};

export default function GoodsTransfers() {
  const { organization, user } = useAuth();
  const { locations } = useLocations();
  const queryClient = useQueryClient();
  const { currentLocation } = useLocationStore();

  // Real-time subscription for goods transfers
  useEffect(() => {
    if (!organization?.id) return;
    const channel = subscribeToGoodsTransfers(organization.id, () => {
      queryClient.invalidateQueries({ queryKey: ['goods-transfers'] });
    });
    return () => { unsubscribe(channel); };
  }, [organization?.id, queryClient]);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<GoodsTransfer | null>(null);
  const [selectedTransferItems, setSelectedTransferItems] = useState<GoodsTransferItem[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    from_location_id: '',
    to_location_id: '',
    expected_date: '',
    notes: '',
  });
  const [formItems, setFormItems] = useState<TransferFormItem[]>([
    { item_key: '', quantity: '' },
  ]);
  const [formError, setFormError] = useState<string | null>(null);

  // Shipping state
  const [shippingNotes, setShippingNotes] = useState('');

  // Receive state
  const [receiveQuantities, setReceiveQuantities] = useState<Record<string, string>>({});
  const [varianceReasons, setVarianceReasons] = useState<Record<string, string>>({});
  const [receivingNotes, setReceivingNotes] = useState('');

  const availabilityLocationId = createDialogOpen
    ? formData.from_location_id
    : shipDialogOpen
      ? selectedTransfer?.from_location_id || ''
      : '';

  // Fetch transfers
  const { data: transfers = [], isLoading } = useQuery({
    queryKey: ['goods-transfers', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase
        .from('goods_transfers')
        .select(`
          *,
          from_location:locations!goods_transfers_from_location_id_fkey(id, name),
          to_location:locations!goods_transfers_to_location_id_fkey(id, name),
          creator:profiles!goods_transfers_created_by_fkey(first_name, last_name)
        `)
        .eq('organization_id', organization.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as GoodsTransfer[];
    },
    enabled: !!organization?.id,
  });

  const {
    data: sourceAvailability = [],
    isLoading: sourceAvailabilityLoading,
    error: sourceAvailabilityError,
    refetch: refetchSourceAvailability,
  } = useQuery({
    queryKey: ['transfer-source-availability', availabilityLocationId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_transfer_source_availability', {
        p_location_id: availabilityLocationId,
      });
      if (error) throw error;
      return (Array.isArray(data) ? data : []) as TransferSourceAvailability[];
    },
    enabled: !!availabilityLocationId,
    staleTime: 15_000,
  });

  const availabilityByItemKey = new Map(
    sourceAvailability.map((row) => [row.item_key, row] as const),
  );

  const hasInvalidCreateQuantity = formItems.some((item) => {
    if (!item.item_key || !item.quantity) return false;
    const requested = Number(item.quantity);
    const available = availabilityByItemKey.get(item.item_key)?.quantity_available ?? 0;
    return !Number.isFinite(requested) || requested <= 0 || requested > available;
  });

  const insufficientShipItems = selectedTransferItems.filter((item) => {
    const available = availabilityByItemKey.get(getTransferItemKey(item))?.quantity_available ?? 0;
    return item.quantity_sent > available;
  });

  // Fetch transfer items
  const fetchTransferItems = async (transferId: string) => {
    const { data, error } = await supabase.rpc('get_goods_transfer_items', {
      p_transfer_id: transferId,
    });
    if (error) throw error;
    return (Array.isArray(data) ? data : []) as GoodsTransferItem[];
  };

  // Create transfer mutation
  const createTransfer = useMutation({
    mutationFn: async () => {
      const itemsToInsert = formItems
        .filter(item => item.item_key || item.quantity)
        .map(item => ({
          item_key: item.item_key,
          quantity_sent: Number(item.quantity),
        }));

      if (itemsToInsert.length === 0 || itemsToInsert.some(item => !item.item_key || !Number.isFinite(item.quantity_sent) || item.quantity_sent <= 0)) {
        throw new Error('Each transfer line needs an inventory item and a quantity greater than zero');
      }

      if (new Set(itemsToInsert.map(item => item.item_key)).size !== itemsToInsert.length) {
        throw new Error('An inventory item can appear only once in a transfer');
      }

      const latestAvailability = await refetchSourceAvailability();
      if (latestAvailability.error) throw latestAvailability.error;
      const latestByItemKey = new Map(
        (latestAvailability.data || []).map((row) => [row.item_key, row] as const),
      );

      const unavailableItem = itemsToInsert.find((item) => {
        const available = latestByItemKey.get(item.item_key)?.quantity_available ?? 0;
        return item.quantity_sent > available;
      });
      if (unavailableItem) {
        const availability = latestByItemKey.get(unavailableItem.item_key);
        throw new Error(
          `${availability?.item_name || 'This item'} has ${formatQuantity(availability?.quantity_available ?? 0)} available at the source location, but ${formatQuantity(unavailableItem.quantity_sent)} was requested.`,
        );
      }

      const { data: transferId, error } = await supabase.rpc('create_goods_transfer', {
        p_from_location_id: formData.from_location_id,
        p_to_location_id: formData.to_location_id,
        p_expected_date: formData.expected_date || null,
        p_notes: formData.notes || null,
        p_items: itemsToInsert.map(item => {
          const [itemType, itemId] = item.item_key.split(':', 2);
          return {
            product_id: itemType === 'product' ? itemId : null,
            raw_item_id: itemType === 'raw' ? itemId : null,
            quantity: item.quantity_sent,
          };
        }),
      });

      if (error) throw error;
      return transferId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goods-transfers'] });
      toast.success('Transfer created', { description: 'The transfer is ready to be shipped' });
      setCreateDialogOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error('Transfer was not created', { description: error.message });
    },
  });

  // Ship transfer mutation - deducts inventory from source location
  const shipTransfer = useMutation({
    mutationFn: async (transferId: string) => {
      // Prepare items for the database function
      const itemsJson = selectedTransferItems.map(item => ({
        item_id: item.id,
        quantity_sent: item.quantity_sent,
      }));

      // Call the database function to deduct inventory from source location
      await invokeAuthenticatedRpc('send_goods_transfer_items', {
        p_transfer_id: transferId,
        p_items: itemsJson,
      });

      // Update shipping notes if provided
      if (shippingNotes) {
        const { error } = await supabase
          .from('goods_transfers')
          .update({
            shipped_by: user?.id,
            shipping_notes: shippingNotes,
          })
          .eq('id', transferId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goods-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
      toast.success('Transfer shipped', { description: 'Inventory has been deducted from source location' });
      setShipDialogOpen(false);
    },
    onError: (error) => {
      toast.error('Transfer was not shipped', { description: error.message });
    },
  });

  // Receive transfer mutation - adds inventory to destination location
  const receiveTransfer = useMutation({
    mutationFn: async (transferId: string) => {
      // Prepare items for the database function
      const itemsJson = selectedTransferItems.map(item => ({
        item_id: item.id,
        quantity_received: parseFloat(receiveQuantities[item.id] || '0'),
      }));

      if (itemsJson.some((item, index) => {
        const current = selectedTransferItems[index].quantity_received ?? 0;
        const sent = selectedTransferItems[index].quantity_sent;
        return !Number.isFinite(item.quantity_received) || item.quantity_received < current || item.quantity_received > sent;
      })) {
        throw new Error('Each received quantity must be between the previously received and shipped quantity');
      }

      // Call the database function to add inventory at destination location
      await invokeAuthenticatedRpc('receive_goods_transfer_items', {
        p_transfer_id: transferId,
        p_items: itemsJson,
      });

      // Metadata must not turn a completed receipt into a retryable error.
      for (const item of selectedTransferItems) {
        if (varianceReasons[item.id]) {
          const { error } = await supabase
            .from('goods_transfer_items')
            .update({ variance_reason: varianceReasons[item.id] })
            .eq('id', item.id);
          if (error) console.error('Receipt recorded but variance reason could not be saved:', error);
        }
      }

      // Update receiving notes if provided
      if (receivingNotes) {
        const { error } = await supabase
          .from('goods_transfers')
          .update({
            received_by: user?.id,
            receiving_notes: receivingNotes,
          })
          .eq('id', transferId);
        if (error) console.error('Receipt recorded but receiving notes could not be saved:', error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goods-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
      toast.success('Transfer received', { description: 'Inventory has been added to your location' });
      setReceiveDialogOpen(false);
    },
    onError: (error) => {
      toast.error('Transfer was not received', { description: error.message });
    },
  });

  // Cancel transfer mutation
  const cancelTransfer = useMutation({
    mutationFn: async (transferId: string) => {
      const { error } = await supabase
        .from('goods_transfers')
        .update({ status: 'cancelled' })
        .eq('id', transferId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goods-transfers'] });
      toast.success('Transfer cancelled');
    },
    onError: (error) => {
      toast.error('Transfer was not cancelled', { description: error.message });
    },
  });

  // Filter transfers by location (if a specific location is selected)
  const filteredTransfers = transfers.filter(transfer => {
    // If a specific location is selected (not "All Locations"), filter by that location
    if (currentLocation?.id) {
      const matchesLocation = 
        transfer.from_location_id === currentLocation.id || 
        transfer.to_location_id === currentLocation.id;
      if (!matchesLocation) return false;
    }
    
    // Apply status filter
    if (statusFilter !== 'all' && transfer.status !== statusFilter) return false;
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        transfer.transfer_number.toLowerCase().includes(query) ||
        transfer.from_location?.name.toLowerCase().includes(query) ||
        transfer.to_location?.name.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Get incoming transfers that need attention (for alerts)
  const incomingTransfers = currentLocation?.id
    ? transfers.filter(t => 
        t.to_location_id === currentLocation.id && 
        t.status === 'in_transit'
      )
    : [];

  const handleCreateTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.from_location_id || !formData.to_location_id) {
      setFormError('Please select both source and destination locations');
      return;
    }

    if (formData.from_location_id === formData.to_location_id) {
      setFormError('Source and destination must be different');
      return;
    }

    const populatedItems = formItems.filter(item => item.item_key || item.quantity);
    if (populatedItems.length === 0) {
      setFormError('Please add at least one item');
      return;
    }

    if (populatedItems.some(item => !item.item_key || !Number.isFinite(Number(item.quantity)) || Number(item.quantity) <= 0)) {
      setFormError('Each transfer line needs an inventory item and a quantity greater than zero');
      return;
    }

    if (new Set(populatedItems.map(item => item.item_key)).size !== populatedItems.length) {
      setFormError('An inventory item can appear only once in a transfer');
      return;
    }

    if (sourceAvailabilityLoading) {
      setFormError('Wait for source inventory availability to finish loading');
      return;
    }

    if (sourceAvailabilityError) {
      setFormError('Source inventory availability could not be loaded. Refresh and try again.');
      return;
    }

    if (hasInvalidCreateQuantity) {
      setFormError('One or more quantities exceed the stock currently available at the source location');
      return;
    }

    createTransfer.mutate();
  };

  const openViewDialog = async (transfer: GoodsTransfer) => {
    setSelectedTransfer(transfer);
    const items = await fetchTransferItems(transfer.id);
    setSelectedTransferItems(items);
    setViewDialogOpen(true);
  };

  const openShipDialog = async (transfer: GoodsTransfer) => {
    setSelectedTransfer(transfer);
    const items = await fetchTransferItems(transfer.id);
    setSelectedTransferItems(items);
    setShippingNotes('');
    setShipDialogOpen(true);
  };

  const openReceiveDialog = async (transfer: GoodsTransfer) => {
    setSelectedTransfer(transfer);
    const items = await fetchTransferItems(transfer.id);
    setSelectedTransferItems(items);
    // Initialize with sent quantities
    const initialQuantities: Record<string, string> = {};
    items.forEach(item => {
      initialQuantities[item.id] = item.quantity_received?.toString() || item.quantity_sent.toString();
    });
    setReceiveQuantities(initialQuantities);
    setVarianceReasons({});
    setReceivingNotes('');
    setReceiveDialogOpen(true);
  };

  const addFormItem = () => {
    setFormItems([...formItems, { item_key: '', quantity: '' }]);
  };

  const removeFormItem = (index: number) => {
    setFormItems(formItems.filter((_, i) => i !== index));
  };

  const updateFormItem = (index: number, field: string, value: string) => {
    const newItems = [...formItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormItems(newItems);
  };

  const handleSourceLocationChange = (locationId: string) => {
    setFormData((current) => ({
      ...current,
      from_location_id: locationId,
      to_location_id: current.to_location_id === locationId ? '' : current.to_location_id,
    }));
    setFormError(null);
  };

  const resetForm = () => {
    setFormData({
      from_location_id: '',
      to_location_id: '',
      expected_date: '',
      notes: '',
    });
    setFormItems([{ item_key: '', quantity: '' }]);
    setFormError(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Goods Transfers</h2>
          <p className="text-sm text-muted-foreground">
            Transfer inventory between locations
            {currentLocation && (
              <span className="ml-1">
                • Showing transfers for <span className="font-medium">{currentLocation.name}</span>
              </span>
            )}
          </p>
        </div>
        <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          New Transfer
        </Button>
      </div>

      {/* Incoming Transfers Alert */}
      {incomingTransfers.length > 0 && (
        <div className="mb-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <Truck className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-blue-900 dark:text-blue-100">
                {incomingTransfers.length} incoming transfer{incomingTransfers.length > 1 ? 's' : ''} awaiting receipt
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                You have transfers in transit that need to be received at this location.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {incomingTransfers.slice(0, 3).map((transfer) => (
                  <Button
                    key={transfer.id}
                    variant="outline"
                    size="sm"
                    className="gap-2 border-blue-300 hover:bg-blue-100 dark:border-blue-700 dark:hover:bg-blue-900"
                    onClick={() => openReceiveDialog(transfer)}
                  >
                    <PackageCheck className="h-4 w-4" />
                    Receive {transfer.transfer_number}
                  </Button>
                ))}
                {incomingTransfers.length > 3 && (
                  <Badge variant="secondary" className="self-center">
                    +{incomingTransfers.length - 3} more
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="flex-1 min-w-[200px] max-w-sm">
          <Label className="mb-1.5 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Transfer # or location"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="min-w-[150px]">
          <Label className="mb-1.5 block">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_transit">In Transit</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="partially_received">Partial</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Transfers Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading transfers...</span>
          </div>
        </div>
      ) : filteredTransfers.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <ArrowLeftRight className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">No transfers found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create a transfer to move inventory between locations
              </p>
              <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                New Transfer
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Transfer #
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Route
                </th>
                <th className="text-center text-sm font-medium text-muted-foreground px-4 py-3">
                  Status
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Created
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Expected
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredTransfers.map((transfer) => {
                const statusConfig = STATUS_CONFIG[transfer.status];
                const StatusIcon = statusConfig.icon;
                return (
                  <tr key={transfer.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openViewDialog(transfer)}
                        className="font-medium text-sm hover:underline"
                      >
                        {transfer.transfer_number}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium">{transfer.from_location?.name}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span>{transfer.to_location?.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={cn('gap-1', statusConfig.color)}>
                        <StatusIcon className="h-3 w-3" />
                        {statusConfig.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(transfer.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {transfer.expected_date 
                        ? new Date(transfer.expected_date).toLocaleDateString() 
                        : '—'}
                    </td>
                    <td className="px-2 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openViewDialog(transfer)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {transfer.status === 'pending' && (
                            <>
                              <DropdownMenuItem onClick={() => openShipDialog(transfer)}>
                                <Send className="h-4 w-4 mr-2" />
                                Ship Transfer
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  if (confirm('Cancel this transfer?')) {
                                    cancelTransfer.mutate(transfer.id);
                                  }
                                }}
                                className="text-red-600"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Cancel
                              </DropdownMenuItem>
                            </>
                          )}
                          {transfer.status === 'in_transit' && (
                            <DropdownMenuItem onClick={() => openReceiveDialog(transfer)}>
                              <PackageCheck className="h-4 w-4 mr-2" />
                              Receive Transfer
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Transfer Dialog */}
      <ResponsiveModal open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <ResponsiveModalContent maxWidth="lg">
          <form onSubmit={handleCreateTransfer}>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>New Goods Transfer</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Create a transfer to move inventory between locations
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>

            <ResponsiveModalBody className="max-h-[60vh] overflow-y-auto">
              <div className="grid gap-4">
                {formError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                    {formError}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From Location *</Label>
                    <Select
                      value={formData.from_location_id}
                      onValueChange={handleSourceLocationChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Source location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((location) => (
                          <SelectItem key={location.id} value={location.id}>
                            {location.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>To Location *</Label>
                    <Select
                      value={formData.to_location_id}
                      onValueChange={(value) => setFormData({ ...formData, to_location_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Destination location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations
                          .filter(l => l.id !== formData.from_location_id)
                          .map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Expected Arrival Date</Label>
                  <Input
                    type="date"
                    value={formData.expected_date}
                    onChange={(e) => setFormData({ ...formData, expected_date: e.target.value })}
                  />
                </div>

                {/* Items */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Items to Transfer</Label>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Available excludes stock already reserved for another purpose.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addFormItem}
                      disabled={!formData.from_location_id}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Item
                    </Button>
                  </div>

                  {!formData.from_location_id && (
                    <div className="rounded-md border border-dashed px-3 py-4 text-sm text-muted-foreground">
                      Select a source location to see current inventory availability.
                    </div>
                  )}

                  {formData.from_location_id && sourceAvailabilityLoading && (
                    <div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading stock at {locations.find((location) => location.id === formData.from_location_id)?.name}…
                    </div>
                  )}

                  {formData.from_location_id && sourceAvailabilityError && (
                    <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                      Inventory availability could not be loaded. Close this window, refresh, and try again.
                    </div>
                  )}

                  {formData.from_location_id && (
                    <div className="space-y-2">
                      {formItems.map((item, index) => {
                        const availability = availabilityByItemKey.get(item.item_key);
                        const requested = Number(item.quantity);
                        const exceedsAvailability = !!item.item_key
                          && !!item.quantity
                          && Number.isFinite(requested)
                          && requested > (availability?.quantity_available ?? 0);
                        return (
                      <div
                        key={index}
                        className={cn(
                          'grid gap-2 rounded-lg border p-3 sm:grid-cols-[minmax(0,1fr)_7rem_auto]',
                          exceedsAvailability && 'border-red-500/50 bg-red-500/5',
                        )}
                      >
                        <div className="min-w-0">
                          <Select
                            value={item.item_key}
                            onValueChange={(value) => updateFormItem(index, 'item_key', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select inventory item" />
                            </SelectTrigger>
                            <SelectContent>
                              {sourceAvailability.map((sourceItem) => {
                                const itemKey = sourceItem.item_key;
                                return (
                                  <SelectItem
                                    key={itemKey}
                                    value={itemKey}
                                    disabled={sourceItem.quantity_available <= 0 && item.item_key !== itemKey}
                                  >
                                    {sourceItem.item_name} · {formatQuantity(sourceItem.quantity_available)} {sourceItem.unit_of_measure} available
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          {item.item_key && (
                            <div className={cn(
                              'mt-2 text-xs',
                              exceedsAvailability ? 'text-red-700 dark:text-red-300' : 'text-muted-foreground',
                            )}>
                              <span className="font-medium text-foreground">
                                {formatQuantity(availability?.quantity_available ?? 0)} {availability?.unit_of_measure || 'each'} available
                              </span>
                              {' '}at the source
                              {(availability?.quantity_reserved ?? 0) > 0 && (
                                <> · {formatQuantity(availability?.quantity_reserved ?? 0)} reserved</>
                              )}
                              {availability?.item_type === 'product' && (
                                <> · {formatQuantity(availability.quantity_boh ?? 0)} BOH / {formatQuantity(availability.quantity_foh ?? 0)} FOH</>
                              )}
                            </div>
                          )}
                        </div>
                        <div>
                          <Input
                            type="number"
                            min="0.001"
                            max={availability?.quantity_available}
                            step="any"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => updateFormItem(index, 'quantity', e.target.value)}
                            aria-invalid={exceedsAvailability}
                          />
                        </div>
                        {formItems.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => removeFormItem(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                        {exceedsAvailability && (
                          <p className="text-xs text-red-700 dark:text-red-300 sm:col-span-3">
                            Reduce this to {formatQuantity(availability?.quantity_available ?? 0)} or less. You cannot transfer more than is currently available.
                          </p>
                        )}
                      </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                  />
                </div>
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="blaze"
                disabled={
                  createTransfer.isPending
                  || sourceAvailabilityLoading
                  || !!sourceAvailabilityError
                  || hasInvalidCreateQuantity
                }
              >
                {createTransfer.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Create Transfer
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* View Transfer Dialog */}
      <ResponsiveModal open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <ResponsiveModalContent maxWidth="lg">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              Transfer {selectedTransfer?.transfer_number}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              <div className="flex items-center gap-2">
                <span>{selectedTransfer?.from_location?.name}</span>
                <ArrowRight className="h-3 w-3" />
                <span>{selectedTransfer?.to_location?.name}</span>
              </div>
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-4">
                {selectedTransfer && (
                  <Badge className={cn('gap-1', STATUS_CONFIG[selectedTransfer.status].color)}>
                    {STATUS_CONFIG[selectedTransfer.status].label}
                  </Badge>
                )}
                {selectedTransfer?.shipped_at && (
                  <span className="text-sm text-muted-foreground">
                    Shipped: {new Date(selectedTransfer.shipped_at).toLocaleString()}
                  </span>
                )}
                {selectedTransfer?.received_at && (
                  <span className="text-sm text-muted-foreground">
                    Received: {new Date(selectedTransfer.received_at).toLocaleString()}
                  </span>
                )}
              </div>

              {/* Items */}
              <div className="rounded-lg border">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left text-sm font-medium text-muted-foreground px-4 py-2">
                        Inventory item
                      </th>
                      <th className="text-right text-sm font-medium text-muted-foreground px-4 py-2">
                        Sent
                      </th>
                      <th className="text-right text-sm font-medium text-muted-foreground px-4 py-2">
                        Received
                      </th>
                      <th className="text-right text-sm font-medium text-muted-foreground px-4 py-2">
                        In transit
                      </th>
                      <th className="text-left text-sm font-medium text-muted-foreground px-4 py-2">
                        Variance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedTransferItems.map((item) => {
                      const variance = item.quantity_received !== null
                        ? item.quantity_received - item.quantity_sent
                        : null;
                      return (
                        <tr key={item.id}>
                          <td className="px-4 py-2">
                            <p className="font-medium text-sm">{getTransferItemName(item)}</p>
                            {getTransferItemSku(item) && (
                              <p className="text-xs text-muted-foreground">{getTransferItemSku(item)}</p>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {item.quantity_sent}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {item.quantity_received ?? '—'}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {selectedTransfer?.status === 'pending'
                              ? '—'
                              : formatQuantity(Math.max(item.quantity_sent - (item.quantity_received ?? 0), 0))}
                          </td>
                          <td className="px-4 py-2">
                            {variance !== null && variance !== 0 && (
                              <div>
                                <span className={cn(
                                  'text-sm font-medium',
                                  variance < 0 ? 'text-red-600' : 'text-green-600'
                                )}>
                                  {variance > 0 ? '+' : ''}{variance}
                                </span>
                                {item.variance_reason && (
                                  <p className="text-xs text-muted-foreground">{item.variance_reason}</p>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {selectedTransfer?.notes && (
                <div>
                  <Label className="text-muted-foreground">Notes</Label>
                  <p className="text-sm mt-1">{selectedTransfer.notes}</p>
                </div>
              )}

              {selectedTransfer?.shipping_notes && (
                <div>
                  <Label className="text-muted-foreground">Shipping Notes</Label>
                  <p className="text-sm mt-1">{selectedTransfer.shipping_notes}</p>
                </div>
              )}

              {selectedTransfer?.receiving_notes && (
                <div>
                  <Label className="text-muted-foreground">Receiving Notes</Label>
                  <p className="text-sm mt-1">{selectedTransfer.receiving_notes}</p>
                </div>
              )}
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Ship Transfer Dialog */}
      <ResponsiveModal open={shipDialogOpen} onOpenChange={setShipDialogOpen}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Ship Transfer</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Confirm shipment of {selectedTransfer?.transfer_number} to {selectedTransfer?.to_location?.name}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-4">
              {/* Items summary */}
              <div className="rounded-lg border p-3">
                <div className="mb-2 grid grid-cols-[minmax(0,1fr)_auto_auto] gap-4 text-xs font-medium uppercase text-muted-foreground">
                  <span>Item</span>
                  <span className="text-right">Ship</span>
                  <span className="text-right">Available now</span>
                </div>
                {selectedTransferItems.map((item) => {
                  const availability = availabilityByItemKey.get(getTransferItemKey(item));
                  const insufficient = item.quantity_sent > (availability?.quantity_available ?? 0);
                  return (
                    <div
                      key={item.id}
                      className={cn(
                        'grid grid-cols-[minmax(0,1fr)_auto_auto] gap-4 border-t py-2 text-sm first:border-0',
                        insufficient && 'text-red-700 dark:text-red-300',
                      )}
                    >
                      <span className="truncate">{getTransferItemName(item)}</span>
                      <span className="text-right font-medium tabular-nums">{formatQuantity(item.quantity_sent)}</span>
                      <span className="text-right font-medium tabular-nums">
                        {sourceAvailabilityLoading
                          ? 'Loading…'
                          : `${formatQuantity(availability?.quantity_available ?? 0)} ${availability?.unit_of_measure || 'each'}`}
                      </span>
                    </div>
                  );
                })}
              </div>

              {!sourceAvailabilityLoading && insufficientShipItems.length > 0 && (
                <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                  This transfer cannot ship because current source availability is lower than the recorded quantity for {insufficientShipItems.map(getTransferItemName).join(', ')}. Adjust or replenish the source stock first.
                </div>
              )}

              <div className="space-y-2">
                <Label>Shipping Notes</Label>
                <Textarea
                  value={shippingNotes}
                  onChange={(e) => setShippingNotes(e.target.value)}
                  placeholder="Any notes about this shipment..."
                  rows={2}
                />
              </div>

              <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg text-sm">
                <p className="font-medium text-amber-700 dark:text-amber-400 mb-1">
                  Important
                </p>
                <p className="text-amber-600 dark:text-amber-500">
                  Shipping this transfer will deduct inventory from {selectedTransfer?.from_location?.name}.
                  The receiving location must confirm receipt before inventory is added.
                </p>
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setShipDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={() => selectedTransfer && shipTransfer.mutate(selectedTransfer.id)}
              disabled={
                shipTransfer.isPending
                || sourceAvailabilityLoading
                || !!sourceAvailabilityError
                || insufficientShipItems.length > 0
              }
            >
              {shipTransfer.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <Truck className="h-4 w-4 mr-2" />
              Confirm Shipment
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Receive Transfer Dialog */}
      <ResponsiveModal open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Receive Transfer</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Confirm receipt of {selectedTransfer?.transfer_number} from {selectedTransfer?.from_location?.name}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-4">
              {/* Receive quantities */}
              <div className="space-y-3">
                {selectedTransferItems.map((item) => {
                  const receivedQty = parseFloat(receiveQuantities[item.id] || '0');
                  const hasVariance = receivedQty !== item.quantity_sent;
                  return (
                    <div key={item.id} className="p-3 rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-sm">{getTransferItemName(item)}</p>
                          <p className="text-xs text-muted-foreground">
                            Expected: {item.quantity_sent}
                          </p>
                        </div>
                        <div className="w-24">
                          <Input
                            type="number"
                            min="0"
                            value={receiveQuantities[item.id] || ''}
                            onChange={(e) => setReceiveQuantities({
                              ...receiveQuantities,
                              [item.id]: e.target.value,
                            })}
                            placeholder="Qty"
                          />
                        </div>
                      </div>
                      {hasVariance && (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-3 w-3 text-amber-500" />
                            <span className={cn(
                              'text-xs font-medium',
                              receivedQty < item.quantity_sent ? 'text-red-600' : 'text-green-600'
                            )}>
                              Variance: {receivedQty - item.quantity_sent > 0 ? '+' : ''}{receivedQty - item.quantity_sent}
                            </span>
                          </div>
                          <Input
                            placeholder="Reason for variance"
                            value={varianceReasons[item.id] || ''}
                            onChange={(e) => setVarianceReasons({
                              ...varianceReasons,
                              [item.id]: e.target.value,
                            })}
                            className="h-8 text-sm"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="space-y-2">
                <Label>Receiving Notes</Label>
                <Textarea
                  value={receivingNotes}
                  onChange={(e) => setReceivingNotes(e.target.value)}
                  placeholder="Any notes about this receipt..."
                  rows={2}
                />
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setReceiveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={() => selectedTransfer && receiveTransfer.mutate(selectedTransfer.id)}
              disabled={receiveTransfer.isPending}
            >
              {receiveTransfer.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <PackageCheck className="h-4 w-4 mr-2" />
              Confirm Receipt
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}
